import { useCallback, useContext, useEffect, useRef } from "react";
import { Observable, Subject, type Subscriber } from "rxjs";
import { retry, takeUntil } from "rxjs/operators";
import {
  callGetCachedScreenAPI,
  compareStepScreenshots,
  delay,
  executeCurlCommand,
  preRequestStatusOf,
  flattenArray,
  getErrorMessage,
  getExecutionData,
  getImageDimensions,
  getInputValues,
  getPromptStepsText,
  getValueByPath,
  loadAndCropImage,
  parseCommands,
  parseGptCommandsResponse,
  parseMarkdownTemplate,
  parsePrompt,
  getRunTemplateTimestamp,
  prepareGetUIElements,
  prepareDuolingoAnswerSheet,
  replaceEnvVars,
  retryOperation,
  trackExceptionOnFirestore,
  trackSentryException,
  transformTemplates,
  uint8ArrayToBase64,
  isUnusableCachedScreen,
  getFormattedDate,
  findDuolingoRequest,
  determineModelVersion,
  isLocalCommand,
  parseLocalAppetizeCommand,
  callChangeProxyState,
  multiplyNumbersInUIHierarchy,
  getSessionMutationAtom,
  shouldUseAppetizeClone,
  getNonOccludedElements,
  withPerfLog,
  resetPerfLogIndent,
  rescaleDigitsInAppetizeCommands,
  resyncStrictCachingIndex,
  isAbstractMarkdownPrompt,
  buildMinimizedCachingBaselineForSync,
  redactBase64ForLog,
} from "@/utils/helpers";
import { v4 as uuidv4 } from "uuid";

import {
  type AudioFrame,
  type BuildData,
  type CachedScreenData,
  type CommandHandlerReturnValue,
  type CompanyData,
  type EnvVar,
  type InputValue,
  type PreRequest,
  type TeardownRequest,
  type PromptStep,
  type RandomValuesTemplates,
  type RunExecutionData,
  type ScreenshotDimensions,
  type ServerExecutionParams,
  type TestSuiteData,
  type VideoFrame,
} from "@/utils/types";
import { useAtomValue } from "jotai";
import {
  appetizeClientAtom,
  appetizeSessionStore,
  buildLogsAtomWithId,
} from "@/atoms";
import { removeCoordinates } from "./helpers";
import useCommandHandler from "../../components/use-command-handler";
import { useStore } from "@tanstack/react-store";

import {
  kDelayAtSessionStartTime,
  kEditorDelayAtSessionStartTime,
} from "@/constants/appetize-constants";
import { type AxiosError, isAxiosError } from "axios";
import Timeout from "await-timeout";
import { type Device, type SimulatorSession } from "@/utils/simulator";
import {
  filterNonEssentialUIData,
  getVisibleItems,
  transformUIData,
} from "../sessions/DeviceContext";
import { AuthContext } from "@/auth/AuthContext";
import { pushMediaToAppetize } from "@/utils/media-helpers";

type ExecutionPath =
  | "instant_tap"
  | "cache"
  | "local_command"
  | "local_command_with_ai_fallback"
  | "ai";

export function useStepObservable({
  buildData,
  companyData,
  testSuite,
}: {
  buildData: BuildData;
  companyData: CompanyData;
  testSuite?: TestSuiteData;
}) {
  const fromEditor = testSuite == null;
  const settingsData = companyData.settings;
  const usingAppetizeClone = shouldUseAppetizeClone({
    settingsData,
    buildData,
    fromEditor,
  });
  const { user } = useContext<any>(AuthContext);

  const sameScreenshotsCounter = useRef(0);
  // Previous-step vs current-step screen comparison, computed once per step
  // right after the cropped screenshot is captured; shared by
  // getServerExecutionParams (prompt flag) and performRepeatedActionsCheck
  // (same-screen failsafe).
  const lastScreenComparisonRef = useRef<{
    strictEqual: boolean;
    lenientMatch: boolean;
  } | null>(null);
  const stepCounterRef = useRef(0);
  const lastSuccessfulStepIdRef = useRef<string | null>(null);
  const extractedLinkFromEmailRef = useRef<string | null>(null);
  const currentCachedStep = useRef(0);
  const gptSteps = useRef<any[]>([]);
  const actionHistoryClaude = useRef<any[]>([]);
  const audioFrames = useRef<AudioFrame[]>([]);
  const videoFrames = useRef<VideoFrame[]>([]);
  const companyDataRef = useRef<CompanyData>(companyData);
  const croppedScreenshots = useRef<any[]>([]);
  const disableAggressiveCachingForNextStep = useRef(false);
  // Running counters for cache observability. `cacheEligibleSteps` counts
  // steps where a cache lookup was attempted (runWithCache && !isLocalCommand);
  // `cacheHits` counts how many of those returned a usable cached screenshot.
  // `cacheSyncAiCalls` counts AI calls during strict caching where the model
  // could have returned a cachingSyncIndex; `cacheSyncHits` counts how many of
  // those calls returned a valid (numeric, >=0) index.
  const cacheEligibleSteps = useRef(0);
  const cacheHits = useRef(0);
  const cacheSyncAiCalls = useRef(0);
  const cacheSyncHits = useRef(0);

  const createSessionMutation = useAtomValue(
    getSessionMutationAtom(testSuite?.deviceProviderSettings)
  );
  const appetizeCloneSessionRef = useRef<SimulatorSession | undefined>(
    createSessionMutation.data
  );
  const appetizeSession = useStore(appetizeSessionStore, (state) => state);
  const currentSessionRef = useRef(appetizeSession);

  const currentSessionStartedAt = useRef(0);
  const commandHandler = useCommandHandler({
    appetizeCloneSession: appetizeCloneSessionRef,
    appetizeSessionWrapper: currentSessionRef,
    gptStepsRef: gptSteps,
    audioFramesRef: audioFrames,
    settingsData: companyData.settings,
    buildData,
    fromEditor,
    videoFramesRef: videoFrames,
    companyData,
    extractedLinkFromEmail: extractedLinkFromEmailRef,
  });
  const currentTemplateTimestamp = useRef<string | null>(null);
  const randomValuesTemplates = useRef<RandomValuesTemplates>({});
  const buildLogsAtom = buildLogsAtomWithId(buildData.id);
  const buildLogsData = useAtomValue(buildLogsAtom);
  const retryCountRef = useRef(0);
  const appetizeClient = useAtomValue(appetizeClientAtom);

  const additionalUserPromptContentRef = useRef<{
    story: string | undefined;
    test: string | undefined;
  }>({ test: undefined, story: undefined });

  useEffect(() => {
    if (
      ["Duolingo", "DuolingoLocalization", "kitchenful"].includes(
        companyData?.name ?? ""
      )
    ) {
      const duolingoRequest: { challenges: any[]; type: string } | undefined =
        findDuolingoRequest(buildLogsData.networkLogs);
      let duolingoAnswersLocal: any[] = [];
      if (duolingoRequest !== undefined) {
        const { challenges, type } = duolingoRequest;

        if (
          type === "test" &&
          additionalUserPromptContentRef.current.test === undefined
        ) {
          duolingoAnswersLocal = prepareDuolingoAnswerSheet(challenges, type);
        }

        if (
          type === "story" &&
          additionalUserPromptContentRef.current.story === undefined
        ) {
          duolingoAnswersLocal = prepareDuolingoAnswerSheet(challenges, type);
        }

        if (duolingoAnswersLocal.length > 0) {
          console.log("SETTING DUOLINGO ANSWERS", duolingoAnswersLocal);

          additionalUserPromptContentRef.current[`${type}`] = `
        List of the quiz questions with correct answers:
        <quiz_answers_sheet>
        ${JSON.stringify(duolingoAnswersLocal, null, 2)}
        </quiz_answers_sheet>`;
        }
      }
    }
  }, [companyData.name, buildLogsData.networkLogs]);

  useEffect(() => {
    if (usingAppetizeClone) {
      appetizeCloneSessionRef.current = createSessionMutation.data;
      if (createSessionMutation.data != null) {
        currentSessionStartedAt.current = createSessionMutation.data.startedAt;
      }
    } else {
      currentSessionRef.current = appetizeSession;
      if (appetizeSession.startedAt != null) {
        currentSessionStartedAt.current = appetizeSession.startedAt;
      }
    }
  }, [appetizeSession, createSessionMutation.data, usingAppetizeClone]);

  useEffect(() => {
    audioFrames.current = buildLogsData.audioFrames;
  }, [buildLogsData.audioFrames]);

  useEffect(() => {
    videoFrames.current = buildLogsData.videoFrames;
  }, [buildLogsData.videoFrames]);

  useEffect(() => {
    companyDataRef.current = companyData;
  }, [companyData]);

  const increaseCurrentCachedStep = useCallback(
    (
      cachedScreens: CachedScreenData[],
      foundCachedScreenshot: CachedScreenData
    ) => {
      const currentlyUsedCachedIndex = cachedScreens.findIndex(
        (value) => value.screenshotUrl === foundCachedScreenshot?.screenshotUrl
      );

      console.log("Current screenshot id", currentlyUsedCachedIndex);

      currentCachedStep.current =
        currentlyUsedCachedIndex !== -1
          ? currentlyUsedCachedIndex + 1
          : currentCachedStep.current + 1;
    },
    []
  );

  const getCachedScreenshot = useCallback(
    async (
      lastScreenshot: string,
      cachedScreens: CachedScreenData[],
      topPixelsToCrop: number,
      nextPromptStepId: string | null = null,
      nextPromptStepNumber: string | null = null
    ) => {
      let NUMBER_OF_SCREENS_COMPARED = 5;
      if (
        settingsData.numberOfCachedScreensCompared &&
        settingsData.numberOfCachedScreensCompared > 0
      ) {
        NUMBER_OF_SCREENS_COMPARED = settingsData.numberOfCachedScreensCompared;
      }

      const strictCaching = settingsData.forceStrictCaching === true;

      const nextCachedScreens = strictCaching
        ? cachedScreens.slice(
            currentCachedStep.current,
            currentCachedStep.current + 1
          )
        : cachedScreens.slice(
            currentCachedStep.current,
            currentCachedStep.current + NUMBER_OF_SCREENS_COMPARED
          );
      let currentlyUsedCachedScreens = strictCaching
        ? nextCachedScreens.filter((value) => value?.wasUsedInCachingAlready !== true)
        : nextCachedScreens.filter(
            (value) =>
              value?.baseIndex === undefined ||
              value?.baseIndex <
                stepCounterRef.current + NUMBER_OF_SCREENS_COMPARED
          );

      if (!strictCaching && nextPromptStepId) {
        // Accept the cached step's stepId in either form: the prompt step's
        // UUID (current save format) or the step number string like "1" /
        // "1.1" (older baselines where the model echoed the step number into
        // step_id instead of the actual id). This lets pre-existing baselines
        // keep matching without forcing a re-run.
        const stepIdMatches = (cachedStepId: string | undefined) => {
          if (cachedStepId == null || cachedStepId.length === 0) return false;
          if (cachedStepId === nextPromptStepId) return true;
          if (
            nextPromptStepNumber != null &&
            nextPromptStepNumber.length > 0 &&
            cachedStepId === nextPromptStepNumber
          ) {
            return true;
          }
          return false;
        };
        currentlyUsedCachedScreens = cachedScreens.filter(
          (data) =>
            stepIdMatches(data.stepId) &&
            data.wasUsedInCachingAlready !== true
        );
      }

      console.log("CACHING DEBUG", {
        cachedScreens,
        currentlyUsedCachedScreens,
        currentStep: stepCounterRef.current,
        currentCachedStep: currentCachedStep.current,
        nextPromptStepId,
        nextPromptStepNumber,
      });

      const pixelsToCrop = {
        top: topPixelsToCrop,
        bottom: 30,
        left: 5,
        right: 5,
      };
      const cachedScreenAPIResults = await withPerfLog(
        "CachedScreenAPI",
        async () =>
          await callGetCachedScreenAPI(
            lastScreenshot,
            currentlyUsedCachedScreens,
            pixelsToCrop,
            buildData.organisationId,
            settingsData.aggressiveCachingEnabled &&
              !disableAggressiveCachingForNextStep.current
          ),
        {
          step: stepCounterRef.current,
          currentCachedStep: currentCachedStep.current,
          using: currentlyUsedCachedScreens?.length,
        }
      );

      console.log("> Caching results", redactBase64ForLog(cachedScreenAPIResults));
      const matchData = cachedScreenAPIResults.find((value) => value.isMatch);
      const matchedCachedScreenshot = matchData?.cachedScreenString;

      const cachedData = currentlyUsedCachedScreens.find(
        (value) =>
          matchedCachedScreenshot != null &&
          value.screenshotUrl === matchedCachedScreenshot
      );

      if (cachedData !== undefined) {
        cachedData.wasUsedInCachingAlready = true;
        cachedData.scaleMultipliers = matchData?.scaleMultipliers;

        increaseCurrentCachedStep(cachedScreens, cachedData);
        const unusableCachedScreen: boolean =
          isUnusableCachedScreen(cachedData) ?? false;

        return unusableCachedScreen ? undefined : cachedData;
      }

      return undefined;
    },
    [
      settingsData.numberOfCachedScreensCompared,
      settingsData.forceStrictCaching,
    ]
  );

  const getExecutionDataForInstantTapOnScreenCenter = (
    screenDimensions: ScreenshotDimensions
  ) => {
    console.log("GETTING COMMANDS FOR INSTANT TAP ON SCREEN CENTER");

    const tapX = Math.floor(screenDimensions.width / 2);
    const tapY = Math.floor(screenDimensions.height / 2);

    return {
      gptCommands: [
        `step_number: N/A`,
        "step_description: Tapping on the center of the screen",
        "actions_description: Tapping on the center of the screen",
        "step_completed: True",
        "reasoning: Tapping on the center of the screen",
        `tabOn: screen center;${tapX};${tapY};text`,
      ],
      appetizeCommands: [`tabOn: x=${tapX} y=${tapY}`],
      ocrOutput: [],
    };
  };

  const getExecutionDataFromCache = (
    foundCachedScreenshot: CachedScreenData,
    envVars: EnvVar[],
    runInputValues: Array<{ key: string; value: string }>
  ) => {
    console.log("GETTING COMMANDS FROM CACHED SCREENSHOT");
    console.log("Found cached screen data", foundCachedScreenshot);

    // Resolve any {{...}} placeholders that we templatized at save time
    // (timestamp, currentDate, env vars, run input values, ...) back to the
    // current run's values. Without this, reasoning / actions_description
    // would carry the baseline run's literal values into action_history and
    // the model would reason against stale data (e.g. an old timestamp).
    const resolveTemplate = (value: string) =>
      parsePrompt({
        prompt: value,
        envVars,
        runInputValues,
        currentTemplateTimestamp,
      });

    const cachedReasoning = resolveTemplate(
      foundCachedScreenshot.reasoning ?? "reasoning: Action taken from cache"
    );

    const gptCommands = [
      cachedReasoning.includes("reasoning")
        ? cachedReasoning
        : `reasoning: ${cachedReasoning}`,
      resolveTemplate(foundCachedScreenshot.gptCommand),
    ];

    const scaleMultipliers = foundCachedScreenshot.scaleMultipliers;
    const appetizeCommands = foundCachedScreenshot.appetizeCommand
      .split("\n")
      .map(resolveTemplate);

    rescaleDigitsInAppetizeCommands(appetizeCommands, scaleMultipliers);

    const cachedActionsDescription: string | undefined =
      foundCachedScreenshot.actionsDescription != null
        ? resolveTemplate(foundCachedScreenshot.actionsDescription)
        : undefined;

    if (cachedActionsDescription !== undefined) {
      gptCommands.push(`actions_description: ${cachedActionsDescription}`);
    }

    const cachedStepCompleted: string = foundCachedScreenshot.stepCompleted
      ? "true"
      : "false";

    gptCommands.unshift(
      ...[
        `step_number: ${
          foundCachedScreenshot.stepNumber ?? currentCachedStep.current
        }`,
        `step_completed: ${cachedStepCompleted}`,
        `step_id: ${foundCachedScreenshot.stepId ?? ""}`,
        "Action taken from cache",
      ]
    ); // add step_number and action taken from cache to beginning of the array

    return {
      gptCommands,
      appetizeCommands,
      ocrOutput: [],
    };
  };

  const uiElementsToUserPromptParser = (promptSteps: PromptStep[]) => {
    const {
      nextPromptStep,
      futurePromptStep,
      futurePlusOnePromptStep,
      futurePlusTwoPromptStep,
    } = getNextAndFuturePromptSteps(promptSteps);

    const nextAndFuturePromptStepsList = [
      nextPromptStep?.plainText,
      futurePromptStep?.plainText,
      futurePlusOnePromptStep?.plainText,
      futurePlusTwoPromptStep?.plainText,
    ];

    console.log("withVision debug log", {
      nextAndFuturePromptStepsList,
      lastSuccessfulStepIdRef: lastSuccessfulStepIdRef.current,
    });

    return {
      nextAndFuturePromptStepsList,
      shouldAddElements: nextAndFuturePromptStepsList.some((stepText) =>
        stepText?.includes(".id")
      ),
    };
  };

  const getServerExecutionParams = async ({
    run,
    actionHistory,
    envVars,
    runInputValues,
    base64screenshot,
    shouldModifyActionHistory,
    observer,
    runs,
    currentRunIndex,
  }: {
    run: RunExecutionData;
    actionHistory: string[];
    envVars: EnvVar[];
    runInputValues: InputValue[];
    base64screenshot: string;
    shouldModifyActionHistory: boolean;
    observer: Subscriber<unknown>;
    runs: RunExecutionData[];
    currentRunIndex: number;
  }): Promise<ServerExecutionParams> => {
    const screenshotDimensions = await getImageDimensions(base64screenshot);
    const currentAndPreviousScreenMatch =
      (settingsData.sameScreenMessageEnabled ?? false) &&
      (lastScreenComparisonRef.current?.lenientMatch ?? false);

    const promptSteps = run.promptSteps;
    const { parsedCommands, parsedPromptSteps } = parseCommands({
      promptSteps,
      shortenCommandStringEnabled:
        settingsData.shortenCommandStringEnabled ?? false,
      shortenCommandStringFromTopEnabled:
        settingsData.shortenCommandStringFromTopEnabled ?? false,
      actionHistory,
      currentTemplateTimestamp,
      envVars,
      runInputValues,
      randomValuesTemplates,
    });

    let uiElements: any[] = [];
    let unparsedUiElements: any[] = [];
    let originalUiElements: string | undefined;
    let homeCalloutContainerPresent = false;

    try {
      console.log("Getting UI elements");
      if (!usingAppetizeClone) {
        unparsedUiElements = await currentSessionRef.current.getData().getUI({
          timeout: 5000,
        });
        const result = await prepareGetUIElements(
          unparsedUiElements,
          currentSessionRef.current?.getData()?.config?.platform,
          screenshotDimensions,
          currentSessionRef.current.getData().device.screen.width,
          currentSessionRef.current.getData().device.screen.height,
          settingsData
        );
        uiElements = result.uiElements;
        unparsedUiElements = result.unparsedUiElements;
        homeCalloutContainerPresent = result.homeCalloutContainerPresent;
        // console.log("Prepared UI elements", { result, unparsedUiElements });
      } else {
        const device = appetizeCloneSessionRef.current?.device as Device;
        const isIOS = device?.platform.toLowerCase() === "ios";
        const temp = await appetizeCloneSessionRef.current?.getUI();
        originalUiElements = settingsData.localizations.enabled
          ? temp
          : undefined;
        const multiplier = screenshotDimensions.width / device.screen.width;

        uiElements = transformUIData(temp, isIOS);
        uiElements = filterNonEssentialUIData(uiElements);
        uiElements = getVisibleItems({
          elements: uiElements,
          deviceDimensions: {
            width: device.screen.width,
            height: device.screen.height,
          },
          allowNonClickableItems: true,
          shouldIncludeKeyboardParentElement: true,
          ignoredElements: settingsData.uiInspector.ignoredElements,
        });
        uiElements = getNonOccludedElements({
          items: uiElements,
        });
        uiElements = multiplyNumbersInUIHierarchy(uiElements, multiplier);

        unparsedUiElements = uiElements;
      }
      // console.log("Unparsed UI elements", { unparsedUiElements });
    } catch (e) {
      const shouldFinishExecution =
        usingAppetizeClone &&
        isAxiosError(e) &&
        (e as AxiosError).status === 500 &&
        (e as AxiosError).code?.includes("ERROR_CODE_22");

      const parentRun = runs.at(-1);

      if (shouldFinishExecution) {
        await emitLastInteractionLog(observer, `${e}`, run);
        if (currentRunIndex === runs.length - 1) {
          emitRunError(e, run, observer);
        } else {
          emitRunError(e, run, observer, parentRun);
        }
        throw e;
      }

      console.error("Error getting UI elements", e);
      // trackSentryException(e);
      uiElements = [];
    }

    const filteredActionHistory = !shouldModifyActionHistory
      ? actionHistory
      : actionHistory.filter(
          (item) => item.startsWith("[Step ") || item.startsWith("remember:")
        );

    return {
      uiElements,
      originalUiElements,
      unparsedUiElements,
      currentAndPreviousScreenMatch,
      tapInstantlyOnScreenCenter: homeCalloutContainerPresent,
      commands: parsedCommands,
      promptSteps: parsedPromptSteps,
      envVars,
      runInputValues,
      actionHistory: filteredActionHistory,
      screenshotDimensions,
      step: stepCounterRef.current,
      testId: run.testId,
      isPartialExecution: run.isPartialExecution ?? false,
    };
  };

  const getExecutionDataFromServer = async ({
    base64screenshot,
    buildId,
    params,
    globalTestTask,
    cachedScreens,
  }: {
    base64screenshot: string;
    buildId: string;
    params: ServerExecutionParams;
    globalTestTask?: string;
    cachedScreens?: CachedScreenData[];
  }) => {
    console.log("GETTING COMMANDS FROM GPT CALL REQUEST");

    const parsedUiElements = uiElementsToUserPromptParser(params.promptSteps);

    let additionalUserPromptContent =
      additionalUserPromptContentRef.current.story ??
      additionalUserPromptContentRef.current.test;

    if (parsedUiElements.shouldAddElements) {
      const uiElementsContent = `---Ui hierarchy (useful when asked to tap on specific id). When giving coordinates based on the UI hierarchy,
        calculate the middle point from the boundaries. Round the coordinates to the nearest integer.\n`;
      additionalUserPromptContent =
        (additionalUserPromptContent ?? "") + uiElementsContent;
    }

    if (settingsData.enableAppHandbook) {
      const handbookText = (settingsData.appHandbook ?? "").trim();
      if (handbookText.length > 0) {
        const handbookBlock = `<app_handbook>\n${handbookText}\n</app_handbook>\n`;
        additionalUserPromptContent =
          handbookBlock + (additionalUserPromptContent ?? "");
      }
    }

    console.log({ parsedUiElements, additionalUserPromptContent });

    const currentDate = getFormattedDate(new Date());
    const requestObject = {
      lambda_flow: "get_next_step",
      current_date: currentDate,
      base64_screenshot: "image_placeholder",
      getUI_elements: parsedUiElements.shouldAddElements
        ? params.uiElements
        : [],
      uiHierarchy: parsedUiElements.shouldAddElements
        ? params.unparsedUiElements
        : [],
      test_task_string:
        globalTestTask != null && globalTestTask.length > 0
          ? globalTestTask
          : JSON.stringify(params.promptSteps, null, 2),
      image_width: params.screenshotDimensions.width - 1, // subtract 1, since appetize allows pixels in range <0, image_dimension - 1>
      image_height: params.screenshotDimensions.height - 1,
      action_history: params.actionHistory,
      orgKey: buildData.organisationId,
      uploadId: buildId,
      platform: buildData.platform,
      executionId: uuidv4(),
      template_images: transformTemplates(
        companyDataRef.current.templates ?? []
      ),
      model_version: determineModelVersion(
        parsedUiElements.nextAndFuturePromptStepsList,
        fromEditor,
        companyData
      ),
      fallbackModel: companyData.fallbackModel,
      utilize_fullTextAnnotation: settingsData.utilizeFullTextAnnotation,
      enableSortingOCR: settingsData.enableSortingOCR,
      enableActionHistoryCut: settingsData.enableActionHistoryCut,
      removeOverlappingText: settingsData.removeOverlappingText,
      currentAndPreviousScreenMatch: params.currentAndPreviousScreenMatch,
      popupDetectionEnabled: settingsData.popupDetectionEnabled,
      ocrProvider: settingsData.ocrProvider,
      modelResponseDoubleCheckEnabled:
        !fromEditor && settingsData.modelResponseDoubleCheckEnabled,
      additionalUserPromptPrefix: additionalUserPromptContent,
      popupDetectionParameters: settingsData.popupDetectionParameters,
      templatesDetectionParameters: settingsData.templatesDetectionParameters,
      disabledButtonsDetectionEnabled:
        settingsData.disabledButtonsDetectionEnabled,
      disabledButtonsDetectionParameters:
        settingsData.disabledButtonsDetectionParameters,
    };

    // Decoupled caching-sync-index classifier (mirrors FastAPI
    // client_session.get_lambda_inputs): gated by an explicit setting so it can be
    // toggled independently of the model name. When enabled we send the flag plus the
    // minimized baseline the dedicated classifier matches against; when disabled we send
    // neither (smaller payload, classifier stays off). The legacy Anthropic
    // caching-sync planner still receives the baseline directly.
    if (
      settingsData.cachingSyncIndexEnabled &&
      cachedScreens != null &&
      cachedScreens.length > 0
    ) {
      (requestObject as Record<string, unknown>).caching_sync_index_enabled = true;
      (requestObject as Record<string, unknown>).sdk_caching_baseline =
        buildMinimizedCachingBaselineForSync(cachedScreens);
    }

    // Snapshot via redactBase64ForLog: requestObject.base64_screenshot is set
    // to the real image on the next line, and DevTools shows the *current*
    // state of an object when expanded — without a redacted clone the log
    // would surface the full base64.
    console.log("requestObject", redactBase64ForLog(requestObject));
    requestObject.base64_screenshot = base64screenshot.split(",")[1];

    return await getExecutionData(requestObject);
  };

  const executeCommandsSequentially = useCallback(
    async ({
      observer,
      currentRunIndex,
      runs,
      appetizeCommandsToExecute,
      gptCommandToDisplay,
      screenshotDimensions,
      uiElements = [],
      usingAppetizeMethods = false,
      nextPromptIsTheLastOne = false,
    }: {
      observer: Subscriber<unknown>;
      currentRunIndex: number;
      runs: RunExecutionData[];
      appetizeCommandsToExecute: string[];
      gptCommandToDisplay: string;
      screenshotDimensions: ScreenshotDimensions;
      uiElements?: any[];
      usingAppetizeMethods?: boolean;
      nextPromptIsTheLastOne?: boolean;
    }) => {
      const run = runs[currentRunIndex];
      const parentRun = runs.at(-1);
      let shouldIncreaseCurrentStep = false;

      const awaitForStableScreen = async () => {
        observer.next({
          type: "STEP_PROCESSING_TEXT_UPDATED",
          data: {
            text: "Waiting for stable screen...",
          },
        });

        await withPerfLog("WaitForStableScreen", async () => {
          if (!usingAppetizeClone) {
            try {
              await currentSessionRef.current.getData().waitForAnimations({
                timeout: settingsData.waitForStableScreenTime,
                imageThreshold: 0.000001,
              });
            } catch (e) {
              // Expected error while waiting for stable screen
            }
          } else {
            await delay(settingsData.waitForStableScreenTime);
          }
        });
      };

      const emitRunSucceededWithMessage = async (message: string) => {
        if (currentRunIndex === runs.length - 1) {
          observer.next({
            type: "RUN_COMPLETED",
            data: {
              status: "succeeded",
              message,
              run,
            },
          });
        } else {
          await awaitForStableScreen();
          observer.next({
            type: "RUN_COMPLETED",
            data: {
              status: "succeeded",
              message,
              parentRun,
              run,
            },
          });
        }
      };

      if (appetizeCommandsToExecute.length === 0 && !observer.closed) {
        shouldIncreaseCurrentStep = true;
      } else {
        observer.next({
          type: "APPETIZE_COMMANDS_TO_EXECUTE",
          data: {
            appetizeCommands: appetizeCommandsToExecute,
          },
        });

        for (let i = 0; i < appetizeCommandsToExecute.length; i++) {
          if (observer.closed) {
            break;
          }

          const cmd = appetizeCommandsToExecute[i];
          const isLastCommand = i === appetizeCommandsToExecute.length - 1;

          if (cmd.toLowerCase().includes("task complete:")) {
            await emitRunSucceededWithMessage(gptCommandToDisplay);
          } else if (cmd.toLowerCase().includes("error detected:")) {
            if (currentRunIndex === runs.length - 1) {
              emitRunError(gptCommandToDisplay, run, observer);
            } else {
              emitRunError(gptCommandToDisplay, run, observer, parentRun);
            }
          } else {
            try {
              if (observer.closed) {
                break;
              }
              console.log("calling command handler with cmd: ", cmd);

              observer.next({
                type: "STEP_PROCESSING_TEXT_UPDATED",
                data: {
                  text: "Executing...",
                },
              });
              const returnValue: CommandHandlerReturnValue | null =
                await retryOperation(
                  async (_) =>
                    await commandHandler({
                      cmd,
                      imageDimensions: {
                        width: screenshotDimensions.width,
                        height: screenshotDimensions.height,
                      },
                      uiElements,
                      observer,
                    }),
                  1000,
                  usingAppetizeClone ? 0 : usingAppetizeMethods ? 0 : 3
                );

              if (returnValue != null) {
                if (returnValue.type === "error") {
                  const errorMessage = returnValue.error;
                  console.log("Error executing command:", returnValue);
                  if (
                    "shouldReplaceLastInteractionLog" in { ...returnValue.data }
                  ) {
                    observer.next({
                      type: "UPDATE_EXECUTION_STEP",
                      data: {
                        screenshot: returnValue.data!.screenshot,
                        gptCommand: errorMessage,
                      },
                    });
                  } else {
                    await emitLastInteractionLog(observer, errorMessage, run);
                  }

                  if (currentRunIndex === runs.length - 1) {
                    emitRunError(errorMessage, run, observer);
                  } else {
                    emitRunError(errorMessage, run, observer, parentRun);
                  }
                }

                let updateExecutionStepData = {};
                if (returnValue.type === "success") {
                  const data = returnValue.data!;
                  if ("appetizeCommands" in data) {
                    appetizeCommandsToExecute[i] =
                      cmd + `;${data.appetizeCommands! as string}`;
                    updateExecutionStepData = {
                      appetizeCommands: appetizeCommandsToExecute,
                    };
                  } else {
                    updateExecutionStepData = returnValue;
                  }
                }

                observer.next({
                  type: "UPDATE_EXECUTION_STEP",
                  data: updateExecutionStepData,
                });
              }

              if (usingAppetizeMethods && nextPromptIsTheLastOne) {
                await emitRunSucceededWithMessage("Successfully executed all steps");
                return false;
              }

              const { nextPromptStep, futurePromptStep } =
                getNextAndFuturePromptSteps(run.promptSteps);
              const nextCommandIsLocalCommand = isLocalCommand(
                nextPromptStep?.plainText ?? ""
              );

              const shouldWaitForStableScreen =
                !nextCommandIsLocalCommand &&
                (!cmd.includes("tap in sequence:") || isLastCommand);

              if (shouldWaitForStableScreen) {
                await awaitForStableScreen();
              }

              if (
                nextCommandIsLocalCommand &&
                !settingsData.delayForLocalCommandsDisabled
              ) {
                await withPerfLog("DelayForLocalCommand", async () => {
                  console.log("Waiting 1s for the screen to stabilize");
                  await delay(1000);
                });
              }

              if (isLastCommand) {
                shouldIncreaseCurrentStep = true;
              }
            } catch (cmdError) {
              console.error("Command execution failed:", cmdError);
              const shouldFinishExecution =
                usingAppetizeClone &&
                isAxiosError(cmdError) &&
                (cmdError as AxiosError).status === 500 &&
                (cmdError as AxiosError).code?.includes("ERROR_CODE_22");

              if (usingAppetizeMethods && !shouldFinishExecution) {
                console.error("usingAppetizeMethods", usingAppetizeMethods);
                throw cmdError;
              }
              await emitLastInteractionLog(observer, `${cmdError}`, run);
              if (currentRunIndex === runs.length - 1) {
                emitRunError(cmdError, run, observer);
              } else {
                emitRunError(cmdError, run, observer, parentRun);
              }
            }
          }
        }
      }

      if (shouldIncreaseCurrentStep) {
        await withPerfLog("RepeatedActionsCheck", async () =>
          performRepeatedActionsCheck(
            run,
            observer,
            parentRun,
            currentRunIndex,
            runs
          )
        );
      }

      return shouldIncreaseCurrentStep;
    },
    [fromEditor, buildData, companyData, usingAppetizeClone]
  );

  const emitRunError = useCallback(
    (
      error,
      run: RunExecutionData,
      observer: Subscriber<unknown>,
      parentRun?: RunExecutionData
    ) => {
      retryCountRef.current = retryCountRef.current + 1;
      trackSentryException(error);
      const blocked = `${error}`.includes("500") || `${error}`.includes("502");
      observer.error({
        type: "RUN_COMPLETED",
        data: {
          status: blocked ? "blocked" : "failed",
          message: `${error}`,
          run,
          parentRun,
        },
      });
    },
    []
  );

  const getBase64Screenshot = useCallback(async () => {
    let base64screenshot: string | undefined;

    if (usingAppetizeClone) {
      base64screenshot = await appetizeCloneSessionRef.current!.getScreenshot();
    } else {
      if (videoFrames.current.length > 0) {
        base64screenshot = uint8ArrayToBase64(
          videoFrames.current[videoFrames.current.length - 1]?.buffer
        );
      }
    }

    return base64screenshot;
  }, [usingAppetizeClone]);

  const getPreRequestsData = useCallback(
    async ({
      run,
      envVars,
      inputValues,
      organisationId,
      observer,
      rememberedValuesOut,
    }: {
      run: RunExecutionData;
      envVars: EnvVar[];
      inputValues: InputValue[];
      organisationId: string;
      observer: Subscriber<unknown>;
      rememberedValuesOut?: Record<string, any>;
    }): Promise<string[]> => {
      let rememberGptSteps: string[] = [];
      const preRequests: PreRequest[] = run.settings.preRequests;
      const internalValues: any = rememberedValuesOut ?? {};

      for (const preRequest of preRequests) {
        const filledCurlCommand = preRequest.curlCommand.replace(
          /{{(.*?)}}/g,
          (_, key: string) => {
            if (key.trim().startsWith("env.")) {
              return (
                envVars.find((envVar) => `env.${envVar.key}` === key.trim())
                  ?.value ?? ""
              );
            }
            return (
              internalValues[key.trim()] ??
              inputValues.find((value) => value.key === key.trim())?.value ??
              ""
            );
          }
        );

        const filledCurlCommandWithHiddenSecretEnv =
          preRequest.curlCommand.replace(/{{(.*?)}}/g, (match, key: string) => {
            // If it's a secret env var, return the original match (keep the template)
            if (key.trim().startsWith("env.")) {
              const envVar = envVars.find(
                (envVar) => `env.${envVar.key}` === key.trim()
              );

              if (envVar?.isSecret) {
                return match; // Return original {{env.SECRET}} format
              }

              return envVar?.value ?? "";
            }

            return (
              internalValues[key.trim()] ??
              inputValues.find((value) => value.key === key.trim())?.value ??
              ""
            );
          });

        const base64screenshot = await getBase64Screenshot();
        observer.next({
          type: "PRE_REQUESTS_INIT",
          data: {
            screenshot: base64screenshot,
          },
        });
        if (preRequest.delayBeforeRequest > 0) {
          await delay(preRequest.delayBeforeRequest);
        }
        const currentTimestamp = Date.now();
        const timestampInMs =
          currentTimestamp - currentSessionStartedAt.current;

        const curlResponse = await executeCurlCommand(
          filledCurlCommand,
          organisationId
        );

        if (isAxiosError(curlResponse)) {
          const error = curlResponse;
          const data = {
            ...(error.response != null && {
              response: {
                status: error.response.status,
                statusText: error.response.statusText,
                headers: error.response.headers,
                data: error.response.data,
              },
            }),
            ...(error.request != null && {
              request: {
                method: error.request.method,
                url: error.request.url,
                headers: error.request.headers,
              },
            }),
          };
          const responseValue = {
            // The status the customer's API answered, not our proxy's 502.
            // See preRequestStatusOf.
            status: preRequestStatusOf(error),
            curl: filledCurlCommandWithHiddenSecretEnv,
            data,
          };
          observer.next({
            type: "PRE_REQUESTS_RESPONSE",
            data: {
              responseValue,
              interactionLogs: [
                {
                  screenshot: base64screenshot!,
                  gptCommands: [preRequest.curlCommand],
                  preRequestResponse: responseValue,
                  relativeTimestamp: timestampInMs,
                  absoluteTimestamp: currentTimestamp,
                },
              ],
            },
          });

          throw curlResponse;
        } else {
          const data = {
            ...(curlResponse != null && {
              response: {
                status: curlResponse.status,
                statusText: curlResponse.statusText,
                headers: curlResponse.headers,
                data: curlResponse.data,
              },
            }),
            ...(curlResponse.request != null && {
              request: {
                method: curlResponse.request.method,
                url: curlResponse.request.url,
                headers: curlResponse.request.headers,
              },
            }),
          };

          const responseValue = {
            curl: filledCurlCommandWithHiddenSecretEnv,
            status: curlResponse.status,
            data,
          };

          observer.next({
            type: "PRE_REQUESTS_RESPONSE",
            data: {
              responseValue,
              interactionLogs: [
                {
                  screenshot: base64screenshot!,
                  gptCommands: [preRequest.curlCommand],
                  preRequestResponse: responseValue,
                  relativeTimestamp: timestampInMs,
                  absoluteTimestamp: currentTimestamp,
                },
              ],
            },
          });
        }

        if (
          preRequest.valuesToRemember != null &&
          preRequest.valuesToRemember.length > 0
        ) {
          const newRememberedValues = preRequest.valuesToRemember.map(
            (valueToRemember) => {
              const value = getValueByPath(curlResponse.data, valueToRemember);
              const valueName = valueToRemember.split(".").at(-1) ?? "";
              internalValues[valueName] = value;
              return `[Step 0] remember: "${valueName}: ${value}"`;
            }
          );
          rememberGptSteps = [...rememberGptSteps, ...newRememberedValues];
        }
      }

      return rememberGptSteps;
    },
    [getBase64Screenshot]
  );

  const runTeardownRequests = useCallback(
    async ({
      run,
      envVars,
      inputValues,
      rememberedValues,
      organisationId,
      observer,
    }: {
      run: RunExecutionData;
      envVars: EnvVar[];
      inputValues: InputValue[];
      rememberedValues: Record<string, any>;
      organisationId: string;
      observer: Subscriber<unknown>;
    }): Promise<void> => {
      const teardownRequests: TeardownRequest[] =
        run.settings?.teardownRequests ?? [];
      if (teardownRequests.length === 0) {
        return;
      }

      for (const teardownRequest of teardownRequests) {
        const resolveTemplates = (
          command: string,
          { revealSecrets }: { revealSecrets: boolean }
        ) =>
          command.replace(/{{(.*?)}}/g, (match, key: string) => {
            const trimmedKey = key.trim();
            if (trimmedKey.startsWith("env.")) {
              const envVar = envVars.find(
                (envVar) => `env.${envVar.key}` === trimmedKey
              );
              if (!revealSecrets && envVar?.isSecret) {
                return match;
              }
              return envVar?.value ?? "";
            }
            return (
              rememberedValues[trimmedKey] ??
              inputValues.find((value) => value.key === trimmedKey)?.value ??
              ""
            );
          });

        const filledCurlCommand = resolveTemplates(teardownRequest.curlCommand, {
          revealSecrets: true,
        });
        const filledCurlCommandWithHiddenSecretEnv = resolveTemplates(
          teardownRequest.curlCommand,
          { revealSecrets: false }
        );

        const base64screenshot = await getBase64Screenshot();
        observer.next({
          type: "TEARDOWN_REQUESTS_INIT",
          data: {
            screenshot: base64screenshot,
          },
        });

        const currentTimestamp = Date.now();
        const timestampInMs =
          currentTimestamp - currentSessionStartedAt.current;

        try {
          const curlResponse = await executeCurlCommand(
            filledCurlCommand,
            organisationId
          );

          if (isAxiosError(curlResponse)) {
            const error = curlResponse;
            const data = {
              ...(error.response != null && {
                response: {
                  status: error.response.status,
                  statusText: error.response.statusText,
                  headers: error.response.headers,
                  data: error.response.data,
                },
              }),
              ...(error.request != null && {
                request: {
                  method: error.request.method,
                  url: error.request.url,
                  headers: error.request.headers,
                },
              }),
            };
            const responseValue = {
              curl: filledCurlCommandWithHiddenSecretEnv,
              // Same unwrapping as the pre-request branch above.
              status: preRequestStatusOf(error),
              data,
            };
            observer.next({
              type: "TEARDOWN_REQUESTS_RESPONSE",
              data: {
                responseValue,
                interactionLogs: [
                  {
                    screenshot: base64screenshot!,
                    gptCommands: [teardownRequest.curlCommand],
                    preRequestResponse: responseValue,
                    relativeTimestamp: timestampInMs,
                    absoluteTimestamp: currentTimestamp,
                  },
                ],
              },
            });
          } else {
            const data = {
              ...(curlResponse != null && {
                response: {
                  status: curlResponse.status,
                  statusText: curlResponse.statusText,
                  headers: curlResponse.headers,
                  data: curlResponse.data,
                },
              }),
              ...(curlResponse.request != null && {
                request: {
                  method: curlResponse.request.method,
                  url: curlResponse.request.url,
                  headers: curlResponse.request.headers,
                },
              }),
            };
            const responseValue = {
              curl: filledCurlCommandWithHiddenSecretEnv,
              status: curlResponse.status,
              data,
            };
            observer.next({
              type: "TEARDOWN_REQUESTS_RESPONSE",
              data: {
                responseValue,
                interactionLogs: [
                  {
                    screenshot: base64screenshot!,
                    gptCommands: [teardownRequest.curlCommand],
                    preRequestResponse: responseValue,
                    relativeTimestamp: timestampInMs,
                    absoluteTimestamp: currentTimestamp,
                  },
                ],
              },
            });
          }
        } catch (e) {
          // Teardown failures shouldn't affect the test outcome; just log them.
          console.error("Teardown request failed:", e);
          const responseValue = {
            curl: filledCurlCommandWithHiddenSecretEnv,
            status: undefined,
            data: { error: `${e}` },
          };
          observer.next({
            type: "TEARDOWN_REQUESTS_RESPONSE",
            data: {
              responseValue,
              interactionLogs: [
                {
                  screenshot: base64screenshot!,
                  gptCommands: [teardownRequest.curlCommand],
                  preRequestResponse: responseValue,
                  relativeTimestamp: timestampInMs,
                  absoluteTimestamp: currentTimestamp,
                },
              ],
            },
          });
        }
      }
    },
    [getBase64Screenshot]
  );

  const emitLastInteractionLog = useCallback(
    async (observer: Subscriber<any>, cmd: string, run: RunExecutionData) => {
      const base64screenshot = await getBase64Screenshot();
      const currentTimestamp = Date.now();
      const timestampInMs = currentTimestamp - currentSessionStartedAt.current;

      observer.next({
        type: "LAST_INTERACTION_LOG",
        data: {
          gptStep: [cmd],
          screenshot: base64screenshot,
          run,
          interactionLog: {
            screenshot: base64screenshot,
            gptCommands: [cmd],
            relativeTimestamp: timestampInMs,
            absoluteTimestamp: currentTimestamp,
          },
        },
      });
      await delay(100);
    },
    [fromEditor, getBase64Screenshot]
  );

  const handleAppetizeErrors = useCallback(
    async ({
      observer,
      currentRunIndex,
      runs,
      errorMessage,
    }: {
      observer: Subscriber<any>;
      currentRunIndex: number | null;
      runs: RunExecutionData[];
      errorMessage: string;
    }) => {
      if (currentRunIndex != null && !observer.closed) {
        trackSentryException(errorMessage);
        await trackExceptionOnFirestore({
          error: errorMessage,
          source: "appetize",
        });
        await emitLastInteractionLog(
          observer,
          errorMessage,
          runs[currentRunIndex]
        );
        retryCountRef.current = retryCountRef.current + 1;
        if (currentRunIndex === runs.length - 1) {
          observer.error({
            type: "RUN_COMPLETED",
            data: {
              status: "blocked",
              message: errorMessage,
              run: runs[currentRunIndex],
            },
          });
        } else {
          observer.error({
            type: "RUN_COMPLETED",
            data: {
              status: "blocked",
              message: errorMessage,
              run: runs[currentRunIndex],
              parentRun: runs[runs.length - 1],
            },
          });
        }
      }
    },
    [emitLastInteractionLog]
  );

  const getNextAndFuturePromptSteps = useCallback(
    (promptSteps: PromptStep[]) => {
      let nextPromptStep;
      let futurePromptStep;
      let futurePlusOnePromptStep;
      let futurePlusTwoPromptStep;

      if (lastSuccessfulStepIdRef.current != null) {
        const lastSuccessfulStepIndex = promptSteps.findIndex(
          (step) => step.id === lastSuccessfulStepIdRef.current
        );
        const sliceStartIndex = lastSuccessfulStepIndex + 1;
        const relativeNextNoSubstepIndex = promptSteps
          .slice(sliceStartIndex)
          .findIndex((step) => !step.isSubStep);

        // Convert relative index to absolute index, or use next step after last successful if not found
        const nextNoSubstepIndex =
          relativeNextNoSubstepIndex === -1
            ? Math.min(sliceStartIndex, promptSteps.length - 1)
            : sliceStartIndex + relativeNextNoSubstepIndex;

        nextPromptStep = promptSteps.at(nextNoSubstepIndex);
        futurePromptStep = promptSteps.at(nextNoSubstepIndex + 1);
        futurePlusOnePromptStep = promptSteps.at(nextNoSubstepIndex + 2);
        futurePlusTwoPromptStep = promptSteps.at(nextNoSubstepIndex + 3);
      } else {
        nextPromptStep = promptSteps.at(0);
        futurePromptStep = promptSteps.at(1);
        futurePlusOnePromptStep = promptSteps.at(2);
        futurePlusTwoPromptStep = promptSteps.at(3);
      }

      return {
        nextPromptStep,
        futurePromptStep,
        futurePlusOnePromptStep,
        futurePlusTwoPromptStep,
      };
    },
    [lastSuccessfulStepIdRef]
  );

  const updateLastSuccessfulStepId = useCallback(
    (stepId: string | undefined) => {
      lastSuccessfulStepIdRef.current = stepId ?? null;
      console.log("> Changing lastSuccessfulStepIdRef to:", stepId);

      if (disableAggressiveCachingForNextStep.current) {
        disableAggressiveCachingForNextStep.current = false;
        console.log("> Resetting disableAggressiveCachingForNextStep to false");
      }
    },
    [lastSuccessfulStepIdRef, disableAggressiveCachingForNextStep]
  );

  const performRepeatedActionsCheck = useCallback(
    async (
      run: RunExecutionData,
      observer: Subscriber<unknown>,
      parentRun: RunExecutionData | undefined,
      currentRunIndex: number,
      runs: string | any[]
    ) => {
      let errorMessage: string | null = null;

      let SAME_SCREENSHOTS_LIMIT = 5;
      let SAME_STEP_PERFORMED_LIMIT = 10;
      if (
        settingsData?.sameStepPerformedLimit &&
        settingsData.sameStepPerformedLimit > 0
      ) {
        SAME_STEP_PERFORMED_LIMIT = settingsData.sameStepPerformedLimit;
        SAME_SCREENSHOTS_LIMIT = settingsData.sameStepPerformedLimit;
      }

      if (
        settingsData?.sameScreenOccurredLimit &&
        settingsData.sameScreenOccurredLimit > 0
      ) {
        SAME_SCREENSHOTS_LIMIT = settingsData.sameScreenOccurredLimit;
      }

      if (
        croppedScreenshots.current.length >= 2 &&
        lastScreenComparisonRef.current != null
      ) {
        const rememberCommandIsUsed = (
          (gptSteps.current.at(-1) as string[]) ?? []
        ).some((value) => value.includes("remember"));

        const screenshotsEqual = lastScreenComparisonRef.current.strictEqual;
        console.log("screenshotsEqual", screenshotsEqual);

        if (screenshotsEqual && !rememberCommandIsUsed) {
          sameScreenshotsCounter.current += 1;
        } else {
          sameScreenshotsCounter.current = 0;
        }

        if (sameScreenshotsCounter.current >= SAME_SCREENSHOTS_LIMIT - 1) {
          errorMessage = `Test failed due to repeated attempts on the same screen (${SAME_SCREENSHOTS_LIMIT}+ times). Please check for blockers or review the test definition.`;
        }
      }

      if (gptSteps.current.length >= SAME_STEP_PERFORMED_LIMIT) {
        const lastGptSteps = gptSteps.current.slice(-SAME_STEP_PERFORMED_LIMIT);
        const lastStepNumbers = lastGptSteps.map(
          (value) => parseGptCommandsResponse(value).step_number
        );
        const allStepNumbersTheSame = lastStepNumbers.every(
          (val, i, arr) => val === arr[0]
        );

        if (allStepNumbersTheSame && lastStepNumbers[0] !== "") {
          errorMessage = `Test failed due to repeated attempts on the same step (${SAME_STEP_PERFORMED_LIMIT}+ times). Please check for blockers or review the test definition.`;
        }
      }

      if (errorMessage) {
        await emitLastInteractionLog(observer, `${errorMessage}`, run);
        if (currentRunIndex === runs.length - 1) {
          emitRunError(errorMessage, run, observer);
        } else {
          emitRunError(errorMessage, run, observer, parentRun);
        }
      }
    },
    [
      emitLastInteractionLog,
      emitRunError,
      settingsData.sameScreenOccurredLimit,
      settingsData.sameStepPerformedLimit,
    ]
  );

  const executeNextSteps = useCallback(
    async (
      runs: RunExecutionData[],
      currentRunIndex: number,
      observer: Subscriber<unknown>,
      envVars: EnvVar[]
    ) => {
      const run = runs[currentRunIndex];
      const parentRun = runs[runs.length - 1];
      const buildId = buildData.id;

      if (!observer.closed) {
        observer.next({
          type: "STEP_PROCESSING_TEXT_UPDATED",
          data: {
            text: "Analysing...",
          },
        });

        try {
          const stepStartTime = performance.now();
          let executionPath: ExecutionPath = "ai";

          // Get step info for header BEFORE logging anything
          const { nextPromptStep: nextPromptStepForMarker } =
            getNextAndFuturePromptSteps(run.promptSteps);
          const nextPromptPlainTextForMarker =
            nextPromptStepForMarker?.plainText ?? "";
          const stepRegex = /^(\d+(?:\.\d+)*)(?:\.)\s.*/;
          const stepNumberForMarker =
            nextPromptStepForMarker?.text.match(stepRegex)?.at(1) ??
            String(stepCounterRef.current);

          // Print step header first
          console.debug(`\n${"=".repeat(60)}`);
          console.debug(
            `STEP ${stepNumberForMarker}: ${nextPromptPlainTextForMarker}`
          );
          console.debug(`${"=".repeat(60)}`);
          resetPerfLogIndent();

          // Now log operations inside the step
          let base64screenshot = await withPerfLog(
            "GetScreenshot",
            async () => (await getBase64Screenshot())!
          );

          observer.next({
            type: "NEW_STEP_INIT",
            data: {
              screenshot: base64screenshot,
            },
          });

          const topPixelsToCrop =
            currentSessionRef.current?.getData()?.config?.platform === "ios"
              ? 80
              : 60;

          await withPerfLog("LoadAndCropImage", async () => {
            croppedScreenshots.current = [
              ...croppedScreenshots.current,
              await loadAndCropImage(base64screenshot, topPixelsToCrop),
            ];
          });

          if (croppedScreenshots.current.length >= 2) {
            const lastScreenshots = croppedScreenshots.current.slice(-2);
            await withPerfLog("CompareStepScreenshots", async () => {
              lastScreenComparisonRef.current = await compareStepScreenshots(
                lastScreenshots[0],
                lastScreenshots[1]
              );
            });
          } else {
            lastScreenComparisonRef.current = null;
          }

          const shouldModifyActionHistory = true;

          let action_history;
          if (!shouldModifyActionHistory) {
            action_history = flattenArray(actionHistoryClaude.current);
          } else {
            const parsedGptSteps: string[][] = gptSteps.current.map(
              (value: string[]) => {
                const {
                  reasoning,
                  commands,
                  step_number,
                  actions_description,
                } = parseGptCommandsResponse(value);

                const action_history_array = [];

                if (actions_description) {
                  action_history_array.push(
                    `[Step ${step_number}] ${actions_description}`
                  );
                }

                if (reasoning && commands && step_number) {
                  action_history_array.push(
                    ...[reasoning, `[Step ${step_number}] ${commands}`]
                  );
                  return action_history_array;
                } else {
                  return value;
                }
              }
            );
            action_history = removeCoordinates(flattenArray(parsedGptSteps));
          }

          const runWithCache: boolean = run.cache?.enabled ?? false;
          let foundCachedScreenshot: CachedScreenData | undefined;
          const cachedScreens: CachedScreenData[] = run.cache?.data ?? [];

          const runInputValues: InputValue[] = getInputValues({
            currentRun: run,
            nextRunsToExecute: runs.slice(currentRunIndex + 1),
            envVars,
          });

          const serverExecutionParams = await withPerfLog(
            "GetServerExecutionParams",
            async () =>
              getServerExecutionParams({
                run,
                envVars,
                runInputValues,
                actionHistory: action_history,
                base64screenshot,
                shouldModifyActionHistory,
                observer,
                runs,
                currentRunIndex,
              }),
            { step: stepCounterRef.current }
          );

          const { nextPromptStep } = await withPerfLog(
            "GetNextAndFuturePromptSteps",
            async () =>
              getNextAndFuturePromptSteps(serverExecutionParams.promptSteps),
            { step: stepCounterRef.current }
          );
          const nextPromptPlainText = nextPromptStep?.plainText ?? "";
          // Templated (un-rendered) `text` for the same step — i.e. with the
          // leading "1. " step number AND `{{...}}` placeholders intact. Cached
          // prompts are persisted from `matchingPromptStep.text` (see
          // use-execute-steps.tsx), so strict-caching prompt comparisons must
          // use this same form. Using `plainText` (which strips the number) or
          // the rendered text (which strips the templates) would mismatch.
          const nextPromptTemplatedText =
            (nextPromptStep?.id != null
              ? run.promptSteps.find((s) => s.id === nextPromptStep.id)?.text
              : undefined) ??
            nextPromptStep?.text ??
            nextPromptPlainText;
          const nextPromptStepNumber =
            nextPromptStep?.text.match(/^(\d+(?:\.\d+)*)(?:\.)\s.*/)?.at(1) ??
            null;

          if (runWithCache && !isLocalCommand(nextPromptPlainText)) {
            cacheEligibleSteps.current += 1;
            foundCachedScreenshot = await withPerfLog(
              "GetCachedScreenshot",
              async () =>
                await getCachedScreenshot(
                  base64screenshot,
                  cachedScreens,
                  topPixelsToCrop,
                  nextPromptStep!.id,
                  nextPromptStepNumber
                ),
              { step: stepCounterRef.current }
            );
            if (foundCachedScreenshot !== undefined) {
              cacheHits.current += 1;
            }
            const cacheRatioPct =
              cacheEligibleSteps.current > 0
                ? Math.round(
                    (cacheHits.current / cacheEligibleSteps.current) * 100
                  )
                : 0;
            const syncRatioPct =
              cacheSyncAiCalls.current > 0
                ? Math.round(
                    (cacheSyncHits.current / cacheSyncAiCalls.current) * 100
                  )
                : 0;
            console.log(
              `> Cache hit ratio: ${cacheHits.current}/${cacheEligibleSteps.current} (${cacheRatioPct}%) | ` +
                `Cache sync hit ratio: ${cacheSyncHits.current}/${cacheSyncAiCalls.current} (${syncRatioPct}%)`
            );
          }

          let shouldIncreaseCurrentStep;
          let jsonResponse;
          const currentTimestamp = Date.now();
          const timestampInMs =
            currentTimestamp - currentSessionStartedAt.current;

          if (serverExecutionParams.tapInstantlyOnScreenCenter) {
            executionPath = "instant_tap";
            jsonResponse = getExecutionDataForInstantTapOnScreenCenter(
              serverExecutionParams.screenshotDimensions
            );
            const parsedExecutionData = await withPerfLog(
              "ParseExecutionData",
              async () =>
                parseExecutionData({
                  envVars,
                  jsonResponse,
                  observer,
                  base64screenshot,
                  run,
                  action_history,
                  serverExecutionParams,
                  currentTimestamp,
                  timestampInMs,
                }),
              { step: stepCounterRef.current }
            );
            shouldIncreaseCurrentStep = await withPerfLog(
              "CommandExecution",
              async () =>
                await executeCommandsSequentially({
                  observer,
                  currentRunIndex,
                  runs,
                  appetizeCommandsToExecute:
                    parsedExecutionData.appetizeCommandsToExecute,
                  gptCommandToDisplay: parsedExecutionData.gptCommandToDisplay,
                  screenshotDimensions:
                    serverExecutionParams.screenshotDimensions,
                }),
              { step: stepCounterRef.current, path: executionPath }
            );
          } else if (foundCachedScreenshot !== undefined) {
            executionPath = "cache";
            jsonResponse = getExecutionDataFromCache(
              foundCachedScreenshot,
              envVars,
              runInputValues
            );

            const parsedExecutionData = await withPerfLog(
              "ParseExecutionData",
              async () =>
                parseExecutionData({
                  envVars,
                  jsonResponse,
                  observer,
                  base64screenshot,
                  run,
                  action_history,
                  serverExecutionParams,
                  currentTimestamp,
                  timestampInMs,
                }),
              { step: stepCounterRef.current }
            );
            shouldIncreaseCurrentStep = await withPerfLog(
              "CommandExecution",
              async () =>
                executeCommandsSequentially({
                  observer,
                  currentRunIndex,
                  runs,
                  appetizeCommandsToExecute:
                    parsedExecutionData.appetizeCommandsToExecute,
                  gptCommandToDisplay: parsedExecutionData.gptCommandToDisplay,
                  screenshotDimensions:
                    serverExecutionParams.screenshotDimensions,
                }),
              { step: stepCounterRef.current, path: executionPath }
            );
          } else {
            const nextPromptIsTheLastOne =
              nextPromptStep?.id === run.promptSteps.at(-1).id;
            const regex = /^(\d+(?:\.\d+)*)(?:\.)\s.*/;
            const stepNumber = nextPromptStep?.text.match(regex)?.at(1);

            const stepAlreadyPresentInActionHistory = action_history.some(
              (item) => item.includes(`[Step ${stepNumber}]`)
            );

            if (
              settingsData.useAppetizeCommands &&
              !!nextPromptPlainText &&
              isLocalCommand(nextPromptPlainText) &&
              !stepAlreadyPresentInActionHistory
            ) {
              executionPath = "local_command";
              if (settingsData.delayForLocalCommandsDisabled) {
                base64screenshot = await withPerfLog(
                  "GetScreenshot",
                  async () => (await getBase64Screenshot())!
                );
              }

              const parsedCommand = parseLocalAppetizeCommand(nextPromptStep);

              jsonResponse = {
                appetizeCommands: [parsedCommand],
                gptCommands: [
                  `step_id: ${nextPromptStep!.id}`,
                  ...(stepNumber != null && stepNumber.length > 0
                    ? [
                        `step_number: ${stepNumber}`,
                        `[Step ${stepNumber}] ${parsedCommand}`,
                      ]
                    : []),
                  `step_completed: false`,
                ],
              };

              const prevGptSteps = [...gptSteps.current];

              try {
                const parsedExecutionData = await withPerfLog(
                  "ParseExecutionData",
                  async () =>
                    parseExecutionData({
                      envVars,
                      jsonResponse,
                      observer,
                      base64screenshot,
                      run,
                      action_history,
                      serverExecutionParams,
                      currentTimestamp,
                      timestampInMs,
                    }),
                  { step: stepCounterRef.current }
                );
                shouldIncreaseCurrentStep = await withPerfLog(
                  "CommandExecution",
                  async () =>
                    executeCommandsSequentially({
                      observer,
                      currentRunIndex,
                      runs,
                      appetizeCommandsToExecute:
                        parsedExecutionData.appetizeCommandsToExecute,
                      gptCommandToDisplay:
                        parsedExecutionData.gptCommandToDisplay,
                      screenshotDimensions:
                        serverExecutionParams.screenshotDimensions,
                      uiElements: serverExecutionParams.unparsedUiElements,
                      usingAppetizeMethods: true,
                      nextPromptIsTheLastOne,
                    }),
                  { step: stepCounterRef.current, path: executionPath }
                );
                updateLastSuccessfulStepId(nextPromptStep!.id);
                prevGptSteps.push(jsonResponse.gptCommands);
              } catch (e) {
                if (!settingsData.enableCommandsAIFallback) {
                  throw e;
                }
                executionPath = "local_command_with_ai_fallback";
                base64screenshot = await withPerfLog(
                  "GetScreenshot",
                  async () => (await getBase64Screenshot())!
                );
                const updatedServerExecutionParams = await withPerfLog(
                  "GetServerExecutionParams",
                  async () =>
                    getServerExecutionParams({
                      run,
                      envVars,
                      runInputValues,
                      actionHistory: action_history,
                      base64screenshot,
                      shouldModifyActionHistory,
                      observer,
                      runs,
                      currentRunIndex,
                    }),
                  { step: stepCounterRef.current, context: "fallback" }
                );

                if (runWithCache) {
                  foundCachedScreenshot = await withPerfLog(
                    "GetCachedScreenshot",
                    async () =>
                      getCachedScreenshot(
                        base64screenshot,
                        cachedScreens,
                        topPixelsToCrop,
                        nextPromptStep!.id,
                        nextPromptStepNumber
                      ),
                    { step: stepCounterRef.current, context: "fallback" }
                  );
                }
                if (foundCachedScreenshot !== undefined) {
                  jsonResponse = getExecutionDataFromCache(
                    foundCachedScreenshot,
                    envVars,
                    runInputValues
                  );
                } else {
                  jsonResponse = await withPerfLog(
                    "GetExecutionDataFromServer",
                    async () =>
                      getExecutionDataFromServer({
                        buildId,
                        base64screenshot,
                        params: updatedServerExecutionParams,
                        globalTestTask: run.globalTestTask,
                        cachedScreens,
                      }),
                    { step: stepCounterRef.current, path: executionPath }
                  );

                  if (settingsData.forceStrictCaching && runWithCache) {
                    const parsedForSync = parseGptCommandsResponse(
                      jsonResponse.gptCommands ?? []
                    );
                    const effectiveSyncIndex =
                      parsedForSync.caching_sync_index ??
                      jsonResponse.cachingSyncIndex;
                    cacheSyncAiCalls.current += 1;
                    if (
                      effectiveSyncIndex != null &&
                      Number.isFinite(Number(effectiveSyncIndex)) &&
                      Number(effectiveSyncIndex) >= 0
                    ) {
                      cacheSyncHits.current += 1;
                    }
                    console.log("> AI cachingSyncIndex received:", {
                      rawFromJsonResponse: jsonResponse.cachingSyncIndex,
                      parsedFromGptCommands: parsedForSync.caching_sync_index,
                      effective: effectiveSyncIndex,
                    });
                    currentCachedStep.current = resyncStrictCachingIndex({
                      cachedScreens,
                      currentCachedStepIndex: currentCachedStep.current,
                      aiPrompt: nextPromptTemplatedText,
                      aiAppetizeCommands: jsonResponse.appetizeCommands ?? [],
                      aiCachingSyncIndex: effectiveSyncIndex,
                      screenWidthPx:
                        updatedServerExecutionParams.screenshotDimensions.width,
                      isAbstract: isAbstractMarkdownPrompt(run.globalTestTask),
                    });
                  }
                }

                gptSteps.current = prevGptSteps;

                const parsedExecutionData = await withPerfLog(
                  "ParseExecutionData",
                  async () =>
                    parseExecutionData({
                      envVars,
                      jsonResponse,
                      observer,
                      base64screenshot,
                      run,
                      action_history,
                      serverExecutionParams,
                      shouldReplaceLastInteractionLog: true,
                      currentTimestamp,
                      timestampInMs,
                    }),
                  { step: stepCounterRef.current }
                );
                shouldIncreaseCurrentStep = await withPerfLog(
                  "CommandExecution",
                  async () =>
                    executeCommandsSequentially({
                      observer,
                      currentRunIndex,
                      runs,
                      appetizeCommandsToExecute:
                        parsedExecutionData.appetizeCommandsToExecute,
                      gptCommandToDisplay:
                        parsedExecutionData.gptCommandToDisplay,
                      screenshotDimensions:
                        serverExecutionParams.screenshotDimensions,
                    }),
                  { step: stepCounterRef.current, path: executionPath }
                );
              }
            } else {
              executionPath = "ai";
              jsonResponse = await withPerfLog(
                "GetExecutionDataFromServer",
                async () =>
                  getExecutionDataFromServer({
                    buildId,
                    base64screenshot,
                    params: serverExecutionParams,
                    globalTestTask: run.globalTestTask,
                    cachedScreens,
                  }),
                { step: stepCounterRef.current, path: executionPath }
              );

              if (settingsData.forceStrictCaching && runWithCache) {
                const parsedForSync = parseGptCommandsResponse(
                  jsonResponse.gptCommands ?? []
                );
                const effectiveSyncIndex =
                  parsedForSync.caching_sync_index ??
                  jsonResponse.cachingSyncIndex;
                cacheSyncAiCalls.current += 1;
                if (
                  effectiveSyncIndex != null &&
                  Number.isFinite(Number(effectiveSyncIndex)) &&
                  Number(effectiveSyncIndex) >= 0
                ) {
                  cacheSyncHits.current += 1;
                }
                console.log("> AI cachingSyncIndex received:", {
                  rawFromJsonResponse: jsonResponse.cachingSyncIndex,
                  parsedFromGptCommands: parsedForSync.caching_sync_index,
                  effective: effectiveSyncIndex,
                });
                currentCachedStep.current = resyncStrictCachingIndex({
                  cachedScreens,
                  currentCachedStepIndex: currentCachedStep.current,
                  aiPrompt: nextPromptTemplatedText,
                  aiAppetizeCommands: jsonResponse.appetizeCommands ?? [],
                  aiCachingSyncIndex: effectiveSyncIndex,
                  screenWidthPx:
                    serverExecutionParams.screenshotDimensions.width,
                  isAbstract: isAbstractMarkdownPrompt(run.globalTestTask),
                });
              }

              const parsedExecutionData = await withPerfLog(
                "ParseExecutionData",
                async () =>
                  parseExecutionData({
                    envVars,
                    jsonResponse,
                    observer,
                    base64screenshot,
                    run,
                    action_history,
                    serverExecutionParams,
                    currentTimestamp,
                    timestampInMs,
                  }),
                { step: stepCounterRef.current }
              );

              shouldIncreaseCurrentStep = await withPerfLog(
                "CommandExecution",
                async () =>
                  executeCommandsSequentially({
                    observer,
                    currentRunIndex,
                    runs,
                    appetizeCommandsToExecute:
                      parsedExecutionData.appetizeCommandsToExecute,
                    gptCommandToDisplay:
                      parsedExecutionData.gptCommandToDisplay,
                    screenshotDimensions:
                      serverExecutionParams.screenshotDimensions,
                  }),
                { step: stepCounterRef.current, path: executionPath }
              );
            }
          }

          const stepDurationMs = Math.round(performance.now() - stepStartTime);
          console.debug(
            `[perf] StepTotal ▸ {durationMs: ${stepDurationMs}, step: ${stepCounterRef.current}, path: '${executionPath}'}`
          );
          console.debug(`${"=".repeat(60)}\n`);

          if (shouldIncreaseCurrentStep && !observer.closed) {
            stepCounterRef.current = stepCounterRef.current + 1;
            await executeNextSteps(runs, currentRunIndex, observer, envVars);
          } else {
            lastSuccessfulStepIdRef.current = null;
            disableAggressiveCachingForNextStep.current = false;
            stepCounterRef.current = 0;
            currentCachedStep.current = 0;
            sameScreenshotsCounter.current = 0;
          }
        } catch (error) {
          console.error("error", error);
          const errorMessage = getErrorMessage(error);
          await emitLastInteractionLog(observer, errorMessage, run);

          if (currentRunIndex === runs.length - 1) {
            emitRunError(errorMessage, run, observer);
          } else {
            emitRunError(errorMessage, run, observer, parentRun);
          }
        }
      }
    },
    [
      buildData.id,
      commandHandler,
      fromEditor,
      executeCommandsSequentially,
      settingsData,
    ]
  );

  const parseExecutionData = ({
    jsonResponse,
    observer,
    envVars,
    base64screenshot,
    action_history,
    run,
    serverExecutionParams,
    shouldReplaceLastInteractionLog = false,
    currentTimestamp,
    timestampInMs,
  }: {
    jsonResponse: any;
    observer: Subscriber<unknown>;
    envVars: EnvVar[];
    serverExecutionParams: ServerExecutionParams;
    run: RunExecutionData;
    action_history: string[];
    base64screenshot: string;
    shouldReplaceLastInteractionLog?: boolean;
    currentTimestamp: number;
    timestampInMs: number;
  }) => {
    let appetizeCommandsToExecute = jsonResponse.appetizeCommands;

    const rememberedValues: Record<string, string> = {};
    for (const historyEntry of action_history) {
      // Try quoted format first: remember: "variableName: value"
      let rememberMatch = historyEntry.match(
        /remember:\s*"([^":]+):\s*([^"]+)"/i
      );
      if (!rememberMatch) {
        // Try unquoted format: remember: variableName: value
        rememberMatch = historyEntry.match(/remember:\s*([^:]+):\s*(.+)/i);
      }
      if (rememberMatch) {
        const [, variableName, value] = rememberMatch;
        rememberedValues[variableName.trim()] = value.trim();
      }
    }

    // Replace remembered values in commands
    if (Object.keys(rememberedValues).length > 0) {
      appetizeCommandsToExecute = appetizeCommandsToExecute.map(
        (command: string) =>
          command.replace(/{{(.*?)}}/g, (match, key) => {
            const trimmedKey = key.trim();
            return rememberedValues[trimmedKey] ?? match;
          })
      );
    }

    // Replace env vars in commands
    if (envVars.length > 0) {
      appetizeCommandsToExecute = appetizeCommandsToExecute.map(
        (value: string) =>
          value.replace(
            /{{(.*?)}}/g,
            (match, key) =>
              replaceEnvVars({
                item: key.trim(),
                envVars,
                revealSecrets: true,
              }) ?? value
          )
      );
    }

    // Collapse consecutive identical "tap in sequence" commands into a single multi-tap
    const reducedCommands: string[] = [];
    let cmdIdx = 0;
    while (cmdIdx < appetizeCommandsToExecute.length) {
      const currentCmd = appetizeCommandsToExecute[cmdIdx];
      if (currentCmd.toLowerCase().startsWith("tap in sequence:")) {
        let count = 1;
        while (
          cmdIdx + count < appetizeCommandsToExecute.length &&
          appetizeCommandsToExecute[cmdIdx + count].toLowerCase() ===
            currentCmd.toLowerCase()
        ) {
          count++;
        }
        reducedCommands.push(count > 1 ? `${currentCmd};${count}` : currentCmd);
        cmdIdx += count;
      } else {
        reducedCommands.push(currentCmd);
        cmdIdx++;
      }
    }
    appetizeCommandsToExecute = reducedCommands;

    gptSteps.current = [...gptSteps.current, jsonResponse.gptCommands];
    actionHistoryClaude.current = jsonResponse.actionHistory ?? [];

    if (settingsData.dynamicActionHistoryEnabled) {
      for (const gptCommand of jsonResponse.gptCommands) {
        const commandLowercase = gptCommand.toLowerCase();
        if (
          commandLowercase.includes("tabon:") &&
          commandLowercase.includes("continue")
        ) {
          console.log(" ==== FLUSHING ACTION HISTORY ==== ");
          gptSteps.current = [];
          actionHistoryClaude.current = [];
        }
      }
    }

    observer.next({
      type: "GPT_RESPONSE",
      data: {
        gptStep: jsonResponse.gptCommands,
        screenshot: base64screenshot,
        actionHistory: action_history,
        screenContent: jsonResponse.ocrOutput,
        run,
        appetizeCommandsToExecute,
        serverExecutionParams,
        shouldReplaceLastInteractionLog,
        interactionLog: {
          screenshot: base64screenshot,
          gptCommands: jsonResponse.gptCommands,
          relativeTimestamp: timestampInMs,
          absoluteTimestamp: currentTimestamp,
          serverExecutionParams,
        },
      },
    });

    const parsedGptCommandsResponse = parseGptCommandsResponse(
      jsonResponse.gptCommands
    );

    if (
      !parsedGptCommandsResponse.fromCache &&
      !parsedGptCommandsResponse.step_completed &&
      parsedGptCommandsResponse.commands.includes("tabOn") &&
      !isAbstractMarkdownPrompt(run.globalTestTask) &&
      !settingsData.forceStrictCaching
    ) {
      console.log("> Disabling aggressive caching for next step");
      disableAggressiveCachingForNextStep.current = true;
    }

    if (parsedGptCommandsResponse.step_completed) {
      updateLastSuccessfulStepId(parsedGptCommandsResponse.step_id);
    }

    return {
      appetizeCommandsToExecute,
      gptCommandToDisplay: parsedGptCommandsResponse.commands,
    };
  };

  const customCreateSession = useCallback(
    async ({
      buildData,
      testSuite,
      observer,
      companyData,
      fromEditor,
      runs,
    }: {
      buildData: BuildData;
      testSuite?: TestSuiteData;
      observer: Subscriber<unknown>;
      companyData: CompanyData;
      fromEditor: boolean;
      runs: RunExecutionData[];
    }) => {
      const parentRun = runs.at(-1)!;

      try {
        return await retryOperation(
          async (_) => {
            const heartbeatElement = document.getElementById("heartbeat");
            if (heartbeatElement != null) {
              const date = new Date();
              heartbeatElement.innerText = `CREATING_GPT_DRIVER_SESSION,${date.getTime()}`;
            }

            return await createSessionMutation.mutateAsync({
              userId: user.uid,
              subscriber: observer,
              buildId: buildData.id,
              bundleId: buildData.bundle!,
              storagePath: buildData.storage_path,
              device: parentRun.device,
              os: parentRun.os,
              platform: parentRun.platform,
              launchParams: parentRun.settings.launchParams,
              mediaFiles: parentRun.settings?.mediaFiles,
              grantPermissions: companyData.settings.autoGrantPermissions,
              language: parentRun.locale?.split("_")?.at(0),
              countryCode: parentRun.locale?.split("_")?.at(1),
              proxy:
                companyData.settings.proxyAppetizeClone ??
                companyData.settings.proxy,
              recordVideo: !fromEditor,
              onCleanup: () => {
                createSessionMutation.reset();
              },
              organisationId: companyData.organisationId,
              ...(testSuite != null && {
                testRunId: parentRun.id,
                testSuite,
              }),
              usePhysicalDevice:
                testSuite?.deviceProviderSettings?.usePhysicalDevice ?? false,
            });
          },
          60000,
          5
        );
      } catch (e) {
        const isBlocked = [404, 429].includes(e?.response?.status ?? -1);
        if (isBlocked) {
          observer.error({
            type: "RUN_COMPLETED",
            data: {
              status: "blocked",
              message: e.message,
              run: runs[runs.length - 1],
            },
          });
        } else {
          throw e;
        }
      }
    },
    [createSessionMutation, user.uid]
  );

  const createObservables = useCallback(
    ({
      runs,
      enableRetry,
    }: {
      runs: RunExecutionData[];
      enableRetry: boolean;
    }) => {
      retryCountRef.current = 0;
      const executionId = uuidv4();

      const steps$ = new Observable((rawObserver) => {
        // Defer RUN_COMPLETED emissions so teardown requests can emit
        // UI events before the subscription closes on the failure path.
        const pendingConclusion: {
          current: { type: "next" | "error"; event: any } | null;
        } = { current: null };

        const flushPendingConclusion = () => {
          const pending = pendingConclusion.current;
          if (pending == null) return;
          pendingConclusion.current = null;
          if (pending.type === "next") {
            rawObserver.next(pending.event);
          } else {
            rawObserver.error(pending.event);
          }
        };

        const observer: Subscriber<unknown> = {
          next: (event: any) => {
            if (event?.type === "RUN_COMPLETED") {
              // If a prior conclusion is still pending (e.g. a delayed
              // RUN_COMPLETED from an unawaited emit on a previous run),
              // flush it now so it isn't lost or attributed to the wrong run.
              if (pendingConclusion.current != null) {
                const prior = pendingConclusion.current;
                pendingConclusion.current = null;
                if (prior.type === "next") {
                  rawObserver.next(prior.event);
                } else {
                  rawObserver.error(prior.event);
                }
              }
              pendingConclusion.current = { type: "next", event };
            } else if (
              !rawObserver.closed &&
              pendingConclusion.current?.type !== "error"
            ) {
              rawObserver.next(event);
            }
          },
          error: (err: any) => {
            // Errors take precedence over a pending success conclusion.
            pendingConclusion.current = { type: "error", event: err };
          },
          complete: () => {
            if (!rawObserver.closed) rawObserver.complete();
          },
          get closed() {
            // Only treat the observer as closed for terminal (error) cases so
            // a delayed success conclusion from a previous run doesn't break
            // out of inner loops in the next run.
            return (
              rawObserver.closed || pendingConclusion.current?.type === "error"
            );
          },
        } as unknown as Subscriber<unknown>;

        let unexpectedAppetizeErrorIntervalId: number;
        let executionTimeoutId: number;
        const runSteps = async () => {
          const parentRun = runs.at(-1)!;
          currentTemplateTimestamp.current = getRunTemplateTimestamp({
            runId: parentRun.id,
            executionId,
            attempt: retryCountRef.current,
          });

          const parsedEnvVars: EnvVar[] =
            settingsData.envVars?.map((envVar) => ({
              ...envVar,
              value: parsePrompt({
                prompt: envVar.value,
                envVars: [],
                runInputValues: [],
                currentTemplateTimestamp,
              }),
            })) ?? [];

          parsedEnvVars.push({
            isSecret: false,
            key: "appBundle",
            value: buildData.bundle ?? "",
            lastEditTimestamp: Math.floor(Date.now() / 1000),
          });

          parsedEnvVars.push({
            isSecret: false,
            key: "appVersion",
            value: buildData.appVersionName ?? "",
            lastEditTimestamp: Math.floor(Date.now() / 1000),
          });

          runs.forEach((run, runIndex) => {
            if (run.globalTestTaskTemplate == null) return;
            const runInputValues = getInputValues({
              currentRun: run,
              nextRunsToExecute: runs.slice(runIndex + 1),
              envVars: parsedEnvVars,
            });
            run.globalTestTask = parseMarkdownTemplate({
              markdown: run.globalTestTaskTemplate,
              envVars: parsedEnvVars,
              runInputValues,
              currentTemplateTimestamp,
              randomValuesTemplates: { current: {} },
            });
          });

          console.log("parsedEnvVars", parsedEnvVars);

          if (retryCountRef.current > 0) {
            stepCounterRef.current = 0;
            currentCachedStep.current = 0;
            sameScreenshotsCounter.current = 0;
            croppedScreenshots.current = [];
            lastScreenComparisonRef.current = null;
            observer.next({
              type: "RUN_RETRY",
            });
            observer.next({
              type: "STEP_PROCESSING_TEXT_UPDATED",
              data: {
                text: "Retrying...",
              },
            });
            console.log("will end session on retry");
            if (usingAppetizeClone) {
              await appetizeCloneSessionRef.current?.stop();
            } else {
              await appetizeClient?.endSession();
            }
            console.log("finished session on retry");

            const returnedPromise = usingAppetizeClone
              ? await customCreateSession({
                  buildData,
                  testSuite,
                  observer,
                  companyData,
                  fromEditor,
                  runs,
                })
              : await Timeout.wrap(appetizeClient?.startSession(), 180000);

            console.log("started new session on retry", returnedPromise);
            if (!usingAppetizeClone) {
              if (appetizeClient.session == null) {
                await trackExceptionOnFirestore({
                  error: "Could not initialize emulator session",
                  source: "appetize",
                });
                observer.error({
                  type: "RUN_COMPLETED",
                  data: {
                    status: "blocked",
                    message: "Could not initialize emulator session",
                    run: runs[runs.length - 1],
                  },
                });
              } else {
                // Push media files after Appetize session retry
                const mediaFilesToPush = runs.at(-1)?.settings?.mediaFiles ?? [];
                if (mediaFilesToPush.length > 0) {
                  await pushMediaToAppetize(appetizeClient.session, mediaFilesToPush);
                }
              }
            }
          }
          await delay(1000);
          if (
            !usingAppetizeClone &&
            currentSessionRef.current.getData?.() == null
          ) {
            observer.next({
              type: "STEP_PROCESSING_TEXT_UPDATED",
              data: {
                text: "Starting session...",
              },
            });
            console.log(
              "will start session on STEP_PROCESSING_TEXT_UPDATED block 1"
            );
            await appetizeClient?.endSession();
            console.log(
              "stopped old session on STEP_PROCESSING_TEXT_UPDATED block 1"
            );
            const returnedPromise = await Timeout.wrap(
              appetizeClient?.startSession(),
              180000
            );
            console.log(
              "session started on STEP_PROCESSING_TEXT_UPDATED block 1",
              returnedPromise
            );
            // Push media files after Appetize session restart
            if (appetizeClient?.session) {
              const mediaFilesToPush = runs.at(-1)?.settings?.mediaFiles ?? [];
              if (mediaFilesToPush.length > 0) {
                await pushMediaToAppetize(appetizeClient.session, mediaFilesToPush);
              }
            }
            await delay(kEditorDelayAtSessionStartTime);
          } else if (usingAppetizeClone) {
            const isSessionRunning =
              await appetizeCloneSessionRef.current?.checkIfSessionIsRunning();
            if (appetizeCloneSessionRef.current == null || !isSessionRunning) {
              observer.next({
                type: "STEP_PROCESSING_TEXT_UPDATED",
                data: {
                  text: "Starting session...",
                },
              });
              console.log(
                "will start session on STEP_PROCESSING_TEXT_UPDATED block 2"
              );
              const returnedPromise = await customCreateSession({
                buildData,
                testSuite,
                observer,
                companyData,
                fromEditor,
                runs,
              });
              console.log(
                "session started on STEP_PROCESSING_TEXT_UPDATED block 2",
                returnedPromise
              );
              await delay(kEditorDelayAtSessionStartTime);
            }
          }

          if (
            !fromEditor &&
            buildData.platform.toLowerCase() === "android" &&
            !usingAppetizeClone
          ) {
            try {
              const base64screenshot = await getBase64Screenshot();
              if (base64screenshot == null) {
                throw new Error("No screenshot found");
              }
              const screenshotDimensions = await getImageDimensions(
                base64screenshot
              );

              const closeBluetoothModalCommand =
                'tabOn.id: "android:id/aerr_close"';
              await commandHandler({
                cmd: closeBluetoothModalCommand,
                imageDimensions: {
                  width: screenshotDimensions.width,
                  height: screenshotDimensions.height,
                },
                uiElements: [],
                observer,
              });
            } catch (e) {
              console.error("Error closing bluetooth modal", e);
            }
          }

          randomValuesTemplates.current = {};

          lastSuccessfulStepIdRef.current = null;
          disableAggressiveCachingForNextStep.current = false;
          gptSteps.current = [];
          extractedLinkFromEmailRef.current = null;
          actionHistoryClaude.current = [];
          let currentRunIndex: null | number = null;

          if (
            usingAppetizeClone &&
            appetizeCloneSessionRef.current?.subscriber == null
          ) {
            appetizeCloneSessionRef.current?.setSubscriber?.(observer);
          }

          const executionTimeoutMs =
            1000 * 60 * 60 * runs.length +
            (settingsData.increased_timeout === true ? 1000 * 60 * 100 : 0);
          console.log(
            `[watchdog] executionTimeout=${executionTimeoutMs / 60000}min ` +
              `(runs.length=${runs.length}, increased_timeout=${settingsData.increased_timeout === true})`
          );
          executionTimeoutId = window.setTimeout(() => {
            handleAppetizeErrors({
              observer,
              currentRunIndex,
              runs,
              errorMessage: "timeout",
            });
          }, executionTimeoutMs);

          if (!usingAppetizeClone) {
            unexpectedAppetizeErrorIntervalId = window.setInterval(async () => {
              if (currentSessionRef.current?.getData?.() == null) {
                await handleAppetizeErrors({
                  observer,
                  currentRunIndex,
                  runs,
                  errorMessage: "The simulator session was disconnected",
                });
                appetizeSessionStore.setState((prev) => ({
                  getData: () => null,
                  startedAt: null,
                }));
              }
            }, 60000);

            currentSessionRef.current
              ?.getData()
              ?.on("disconnect", async (_: any) => {
                await handleAppetizeErrors({
                  observer,
                  currentRunIndex,
                  runs,
                  errorMessage: "The simulator session was disconnected",
                });
                appetizeSessionStore.setState((prev) => ({
                  getData: () => null,
                  startedAt: null,
                }));
              });

            currentSessionRef.current?.getData()?.on("error", (error) => {
              handleAppetizeErrors({
                observer,
                currentRunIndex,
                runs,
                errorMessage: getErrorMessage(error),
              });
            });
          }

          for (const runIndex of runs.map((v, i) => i)) {
            croppedScreenshots.current = [];
            lastScreenComparisonRef.current = null;
            if (settingsData.shouldResetActionHistory) {
              gptSteps.current = [];
              actionHistoryClaude.current = [];
            } else {
              gptSteps.current = gptSteps.current.reduce((prev, current) => {
                // Check if 'current' is a string, if so, wrap it in an array
                const currentArray: string[] =
                  typeof current === "string" ? [current] : current;

                const rememberGptStep = currentArray.filter(
                  (gptStep: string) =>
                    gptStep.toLowerCase().includes("remember:") &&
                    !gptStep.startsWith("reasoning:")
                );

                if (rememberGptStep.length > 0) {
                  console.log("rememberGptStep", rememberGptStep);

                  const resetPrevSteps = rememberGptStep.map(
                    (rememberGptStep) =>
                      rememberGptStep.startsWith("[Step 0]")
                        ? rememberGptStep
                        : `[Step 0] ${rememberGptStep}`
                  );
                  return [...prev, resetPrevSteps];
                }
                return prev;
              }, []);
            }

            let base64screenshot: string | undefined;
            try {
              console.log("videoFrames.current", videoFrames.current?.length);
              await retryOperation(
                async (executedTimes) => {
                  if (
                    executedTimes > 0 &&
                    videoFrames.current.length === 0 &&
                    !usingAppetizeClone
                  ) {
                    observer.next({
                      type: "STEP_PROCESSING_TEXT_UPDATED",
                      data: {
                        text: "Starting session...",
                      },
                    });
                    console.log(
                      "will start session on STEP_PROCESSING_TEXT_UPDATED block"
                    );
                    await appetizeClient?.endSession();
                    const returnedPromise = await Timeout.wrap(
                      appetizeClient?.startSession(),
                      40000
                    );
                    console.log(
                      "session started on STEP_PROCESSING_TEXT_UPDATED block",
                      returnedPromise
                    );
                    // Push media files after Appetize session restart
                    if (appetizeClient?.session) {
                      const mediaFilesToPush = runs.at(-1)?.settings?.mediaFiles ?? [];
                      if (mediaFilesToPush.length > 0) {
                        await pushMediaToAppetize(appetizeClient.session, mediaFilesToPush);
                      }
                    }
                    await delay(kEditorDelayAtSessionStartTime);
                  }
                  if (videoFrames.current.length === 0 && !usingAppetizeClone) {
                    throw new Error("No video frames available");
                  }
                  base64screenshot = await getBase64Screenshot();
                },
                2000,
                3
              );
            } catch (e) {}

            if (base64screenshot == undefined) {
              await handleAppetizeErrors({
                observer,
                currentRunIndex: runIndex,
                runs,
                errorMessage: "No video frames available",
              });
              break;
            }

            currentRunIndex = runIndex;
            const currentRun = runs[runIndex];
            const hasPreRequestsToMake =
              currentRun?.settings?.preRequests?.length > 0 &&
              currentRun?.disablePreRequests !== true;
            const parentRun =
              runIndex === runs.length - 1 ? undefined : runs.at(-1);

            observer.next({
              type: "RUN_EXECUTION_STARTED",
              data: {
                runIndex,
                title: runs[runIndex].title,
                commands: getPromptStepsText(runs[runIndex].promptSteps),
                promptSteps: runs[runIndex].promptSteps,
                hasPreRequestsToMake,
                screenshot: base64screenshot,
                testId: runs[runIndex].testId,
              },
            });
            if (currentRunIndex === 0 && !fromEditor) {
              await delay(kDelayAtSessionStartTime);
            }

            const preRequests = currentRun?.settings?.preRequests;
            const runInputValues: InputValue[] = getInputValues({
              currentRun: runs[runIndex],
              nextRunsToExecute: runs.slice(runIndex + 1),
              envVars: parsedEnvVars,
            });
            const preRequestRememberedValues: Record<string, any> = {};
            if (
              preRequests?.length > 0 &&
              currentRun?.disablePreRequests !== true
            ) {
              observer.next({
                type: "STEP_PROCESSING_TEXT_UPDATED",
                data: {
                  text: `Executing pre-request${
                    preRequests.length > 1 ? "s" : ""
                  }...`,
                },
              });
              try {
                const preRequestsGptSteps = await getPreRequestsData({
                  run: currentRun,
                  envVars: parsedEnvVars,
                  inputValues: runInputValues,
                  organisationId: buildData?.organisationId,
                  observer,
                  rememberedValuesOut: preRequestRememberedValues,
                });

                gptSteps.current = [
                  ...gptSteps.current,
                  ...preRequestsGptSteps,
                ];
              } catch (e) {
                console.error("Error in pre-request", e);

                await runTeardownRequests({
                  run: currentRun,
                  envVars: parsedEnvVars,
                  inputValues: runInputValues,
                  rememberedValues: preRequestRememberedValues,
                  organisationId: buildData?.organisationId,
                  observer: rawObserver,
                });

                emitRunError(
                  "Pre-request failed",
                  currentRun,
                  observer,
                  parentRun
                );
                flushPendingConclusion();
                break;
              }
            }
            await executeNextSteps(runs, runIndex, observer, parsedEnvVars);
            await runTeardownRequests({
              run: currentRun,
              envVars: parsedEnvVars,
              inputValues: runInputValues,
              rememberedValues: preRequestRememberedValues,
              organisationId: buildData?.organisationId,
              observer: rawObserver,
            });
            flushPendingConclusion();
            if (rawObserver.closed) {
              break;
            }
          }
          flushPendingConclusion();
          if (!rawObserver.closed) {
            rawObserver.complete();
          }
        };

        runSteps();
        return () => {
          clearTimeout(executionTimeoutId);
          clearInterval(unexpectedAppetizeErrorIntervalId);
        };
      });

      const stop$ = new Subject();
      const stopExecutionHandler = () => {
        stop$.next(1);
        stop$.complete();
      };
      return {
        stopObservable: stopExecutionHandler,
        mainObservable: steps$.pipe(
          takeUntil(stop$),
          retry(enableRetry ? settingsData.numberOfRetriesOnFailure : 0)
        ),
      };
    },
    [
      executeNextSteps,
      settingsData.shouldResetActionHistory,
      settingsData.numberOfRetriesOnFailure,
      appetizeClient,
      fromEditor,
      buildData,
      companyData,
      usingAppetizeClone,
      user?.id,
    ]
  );

  return { createObservables };
}
